import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UserService } from '../../../../../admin/src/app/services/user.service';
import { AuthService } from '../../../../../admin/src/app/services/auth.service';
import { ZendeskService } from '../../services/zendesk.service';
import { AppValidationsHelper } from '../../../../../admin/src/app/app-validations.helper';
import { Request, FileUploadResponse } from '../../models/request';
import { RoutingStateService } from '../../services/routing-state.service';
import { LanguageService } from '../../services/language.service';
import { TrainersService } from '../../services/trainers.service';
import { Trainer } from '../../models/trainers';
import { TranslateService } from '@ngx-translate/core';
import { MatomoTracker } from 'ngx-matomo';
import { METAPIService } from '../../services/metapi.service';

const QuboDigitalWirelessCode = 1;
const QuboDigitalSmartBPlusCode = 71;
const DrivoCode = 72;
const QuboDigitalInteractiveCode = 77;
const AlenoCode = 84;

@Component({
  selector: 'app-ticket',
  templateUrl: './ticket.component.html',
  styleUrls: ['./ticket.component.css']
})
export class TicketComponent implements OnInit {

  form: FormGroup;

  completed: boolean = false;

  loading: boolean = false;

  missingSerialCode: boolean = false;

  invalidSerialCode: boolean = false;

  private file?: File;

  trainers: Trainer[] = [];

  productTypes = [
    {
      id: 'OTHER',
      label: 'OTHER'
    }
  ];

  productPurchased = [
    {
      id: 'NO',
      label: 'No'
    },
    {
      id: 'YES',
      label: 'Yes'
    }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private routingState: RoutingStateService,
    private authSvc: AuthService,
    private userSvc: UserService,
    private zendeskSvc: ZendeskService,
    private trainersSvc: TrainersService,
    public language: LanguageService,
    public translate: TranslateService,
    private matomoTracker: MatomoTracker,
    private metApiSvc: METAPIService,
  ) { }

  ngOnInit() {
    this.matomoTracker.trackPageView('Ticket');

    this.translate
      .get(['YES', 'NO', 'OTHER'])
      .subscribe(data => {
        this.productTypes[0].label = data['OTHER'];
        this.productPurchased[0].label = data['NO'];
        this.productPurchased[1].label = data['YES'];
      });

    this.form = this.fb.group({
      emails: this.fb.group({
        email: [null, [Validators.compose([Validators.required, Validators.email])]],
        confirmEmail: [null, [Validators.compose([Validators.required, Validators.email])]],
      }, {validator: this.matchValidator}),
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      subject: [null, Validators.required],
      description: [null, Validators.required],
      product: this.fb.group({
        productType: [null, Validators.required],
        productPurchased: [null]
      }, {validator: this.serialValidator}),
      serial: [null],
    });

    if (!!this.authSvc.getCurrentToken()) {
      this.userSvc.getCurrentUser().subscribe(data => {
        const tmp: any = data;
        tmp.payload.emails = {
          email: tmp.payload.email
        };
        this.form.reset(tmp.payload);
      });
    }

    this.trainersSvc.getTrainers().subscribe(results => {
      const trainers = results.trainers;
      trainers.sort((i1, i2) => {
        return i1.name.toLowerCase() < i2.name.toLowerCase() ? -1 : 1;
      });
      this.trainers = trainers;
      trainers.forEach(trainer => {
        this.productTypes.push({
          id: '' + trainer.qr_code,
          label: trainer.name
        })
      });
    }, _ => {
    });
  }

  matchValidator(group: FormGroup) {
    for (const name in group.controls) {
      if (group.controls.hasOwnProperty(name)) {
        if (!group.controls[name].valid) {
          return null;
        }
      }
    }

    let email = '';
    let confirmEmail = '';

    for (const name in group.controls) {
      if (group.controls.hasOwnProperty(name)) {
        if (name === 'email') {
          email = group.controls[name].value;
        } else if (name === 'confirmEmail') {
          confirmEmail = group.controls[name].value;
        }
      }
    }

    if (email !== confirmEmail) {
      return {
        mismatch: true
      };
    }

    return null;
  }

  serialValidator(group: FormGroup) {
    for (const name in group.controls) {
      if (group.controls.hasOwnProperty(name)) {
        if (!group.controls[name].valid) {
          return null;
        }
      }
    }

    let productType = '';
    let purchased = '';

    for (const name in group.controls) {
      if (group.controls.hasOwnProperty(name)) {
        if (name === 'productType') {
          productType = group.controls[name].value;
        } else if (name === 'productPurchased') {
          purchased = group.controls[name].value;
        }
      }
    }

    if (!productType || productType == 'OTHER') {
      return null;
    }

    let hasErrors = false;
    let errors = {}

    if (!purchased) {
      hasErrors = true;
      errors['missingValue'] = true;
    }

    if (hasErrors) {
      return errors;
    } else {
      return null;
    }
  }

  updateFile(event: any) {
    this.file = event.srcElement.files[0];
  }

  createTicket() {
    this.form.setErrors(null);
    this.form.markAsDirty();
    this.form.markAsTouched();

    this.missingSerialCode = false;
    this.invalidSerialCode = false;

    this.checkSerialCode();

    if (this.form.valid && this.missingSerialCode === false && this.invalidSerialCode === false) {
      this.loading = true;
      this.uploadFile();
    } else {
      this.loading = false;
      AppValidationsHelper.validateFormFields(this.form);
    }

    return false;
  }

  uploadFile() {
    if (this.file) {
      this.zendeskSvc.uploadFile(this.file)
      .toPromise()
      .then(data => {
        if ((data as FileUploadResponse).upload !== undefined) {
          this.sendRequest((data as FileUploadResponse).upload.token);
          console.log('File uplaoded', data);
        } else {
          this.loading = false;
          this.form.setErrors({ unexpectedError: true });
          console.error('Error on file upload', data);
        }
      }, error => {
        this.loading = false;
        this.form.setErrors({ unexpectedError: true });
        console.error('Error on file upload', error);
      });
    } else {
      this.sendRequest(null);
    }
  }

  sendRequest(uploadToken?: string) {
    let request = <Request>{
      requester: {
        name: `${this.form.value.firstName} ${this.form.value.lastName}`,
        email: this.form.value.emails.email
      },
      subject: this.form.value.subject,
      comment: { body: this.form.value.description }
    };

    if (uploadToken) {
      request.comment.uploads = [uploadToken];
    }

    const serial = this.form.value.serial;
    if (serial) {
      request.custom_fields = [
        {
          id: 24666471,
          value: '' + serial
        }
      ];
    }

    this.zendeskSvc.createRequest(request)
    .toPromise()
    .then(data => {
      this.loading = false;
      this.completed = true;
      console.log('Created new ticket', request,  data);
    }, error => {
      this.loading = false;
      this.form.setErrors({ unexpectedError: true });
      console.error('Error on ticket creation', request, error);

      const data = {
        site: 'support',
        page: 'zendesk',
        logs: {
          request: JSON.stringify(request),
          error: JSON.stringify(error)
        }
      };
      this.metApiSvc.postLogs(data)
      .toPromise()
      .then(data => {}, error => {});
    });
  }

  goBack() {
    let previousUrl = this.routingState.getPreviousUrl();
    if (previousUrl === '/register') {
      previousUrl = '/';
    }
    this.router.navigate([previousUrl]);
  }

  clearFile() {
    this.file = null;
  }

  checkSerialCode() {
    const productType = this.form.value.product.productType;
    if (!productType || productType === 'OTHER') {
      return;
    }

    const productPurchased = this.form.value.product.productPurchased;
    if (!productPurchased || productPurchased == 'NO') {
      return;
    }

    const trainersFiltered = this.trainers.filter(trainer => {
      return '' + trainer.qr_code == this.form.value.product.productType
    });
    if (trainersFiltered.length == 0) {
      return;
    }
    const trainer = trainersFiltered[0];
    const serialLetters = trainer.serial_letters || [];

    if (serialLetters.length == 0 && trainer.code !== QuboDigitalWirelessCode && trainer.code !== QuboDigitalInteractiveCode) {
      // email "Gestione seriale in apertura ticket" del 18/01/2023
      return;
    }

    let res = false;
    let crcValid = true;
    let serial = this.form.value.serial || '';
    let serialToValidate = '';

    if (serial.length === 13) {
      const crc = serial.substring(9);
      serialToValidate = serial.substring(0, 9);
      if (crc !== this.calculateCrc(serialToValidate)) {
        crcValid = false;
      }
    } else {
      serialToValidate = serial;
    }

    if (serialToValidate.length === 9 && serialLetters.length > 0) {
      const first = serial.substr(0, 2);
      const second = serial.substr(2, 1);
      const third = serial.substr(3, 6);
      let firstValid = false;
      let secondValid = true;
      let thirdValid = true;

      for (let i = 0; i < serialLetters.length; i++) {
        if (serialLetters[i] == first) {
          firstValid = true;
          break;
        }
      }

      for (let i = 0; i < second.length; i++) {
        if (second[i] < 'A' || second[i] > 'Z') {
          secondValid = false;
          break;
        }
      }

      for (let i = 0; i < third.length; i++) {
        if (third[i] < '0' || third[i] > '9') {
          thirdValid = false;
          break;
        }
      }

      if (firstValid && secondValid && thirdValid && crcValid) {
        res = true;
      }
    }

    if (!res && (trainer.code === DrivoCode ||
      trainer.code === QuboDigitalSmartBPlusCode ||
      trainer.code === AlenoCode ||
      trainer.code === QuboDigitalInteractiveCode ||
      trainer.code === QuboDigitalWirelessCode)) {
        res = this.validateNumericSerial(serialToValidate, trainer.code)
    }

    if (!res) {
      if (serialToValidate.length === 0) {
        this.missingSerialCode = true;
      } else {
        this.invalidSerialCode = true;
      }
    }
  }

  private validateNumericSerial(serial: string, trainerCode: number): boolean {
    if (serial.length === 0) {
      return false;
    }
    if (trainerCode === QuboDigitalWirelessCode) {
      if (serial[0] !== 'Q') {
        return false;
      }
      serial = serial.substring(1);
      if (serial.length === 0) {
        return false;
      }
    }
    for (let i = 0; i < serial.length; i++) {
      if (serial[i] < '0' || serial[i] > '9') {
        return false;
      }
    }
    return true;
  }

  getSerialKBUrl() {
    if (this.language.getLang() === 'it') {
      return 'https://elitesrl.zendesk.com/hc/it/articles/360013874379-Come-trovare-il-seriale-'
    } else {
      return 'https://elitesrl.zendesk.com/hc/en-us/articles/360013874379-How-to-find-the-serial-number';
    }
  }

  calculateCrc(data: string) {
    let crc = 0;
    let count = data.length;
    for (let i = 0; i < count; i++) {
      crc ^= data.charCodeAt(i);
      for (let j = 0; j < 8; j++) {
        if ((crc & 0x01) === 1) {
          crc = (crc >> 1) ^ 0x6C49;
        } else {
          crc = crc >> 1;
        }
      }
    }
    let result = crc.toString(16);
    while (result.length < 4) {
      result = '0' + result;
    }
    return result.toUpperCase();
  }
}
